import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { catchError, lastValueFrom } from "rxjs";
import { SpinnerService } from "../../../components/spinner/spinner.service";
import { ConfirmService } from "../../../components/confirm-dialog/confirm-dialog.service";
import { SdkDto, SdkStatusEnum } from "../../../dto";
import { HttpSdkService } from "../../../services/sdk/http-sdk.service";

@Injectable({
	providedIn: 'root'
})
export class SdkActionService {

	constructor(
		private httpService: HttpSdkService,
		private toastr: ToastrService,
		private spinnerService: SpinnerService,
		private confirmService: ConfirmService
	) { }

	async disableSdk(input: SdkDto): Promise<boolean> {
		const confirmed = await this.confirmService.show({
			title: 'Disable SDK',
			text: `Are you sure you wish to disable: ${input.name}?`,
			subText: `Packaging will no longer be available for ${input.name}.`,
			yesText: 'Disable',
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		const payload = { ...input, status: SdkStatusEnum.Disabled };

		this.spinnerService.show('Disabling SDK...');
		try {
			const result = await lastValueFrom(
				this.httpService.sdkAction(payload, 'disable').pipe(
					catchError(err => {
						console.error('Error disabling sdk', err);
						this.toastr.error('Failed to disable sdk: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('SDK disabled successfully');
				return true;
			} else {
				this.toastr.error(result.error ? result.error : 'Failed to disable SDK');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

	async enableSdk(input: SdkDto): Promise<boolean> {
		const confirmed = await this.confirmService.show({
			title: 'Enable SDK',
			text: `Are you sure you wish to enable: ${input.name}?`,
			subText: `Packaging will be available for ${input.name}.`,
			yesText: 'Enable',
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		const payload = { ...input, status: SdkStatusEnum.Active };

		this.spinnerService.show('Enabling SDK...');
		try {
			const result = await lastValueFrom(
				this.httpService.sdkAction(payload, 'enable').pipe(
					catchError(err => {
						console.error('Error enabling SDK', err);
						this.toastr.error('Failed to enable SDK: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('SDK enabled successfully');
				return true;
			} else {
				this.toastr.error(result.error ? result.error : 'Failed to enable SDK');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

	async deleteSdk(input: SdkDto): Promise<boolean> {
		const confirmed = await this.confirmService.show({
			title: 'Delete SDK',
			text: `Are you sure you wish to disable: ${input.name}?`,
			subText: `The SDK will no longer be available for packaging, and will be deleted from existing packages.`,
			yesText: 'Delete',
			noText: 'Cancel'
		});

		if (!confirmed) return false;

		this.spinnerService.show('Deleting SDK...');
		try {
			const result = await lastValueFrom(
				this.httpService.sdkAction(input, 'delete').pipe(
					catchError(err => {
						console.error('Error deleting sdk', err);
						this.toastr.error('Failed to delete sdk: ' + (err.error?.error ?? err.message));
						throw err;
					})
				)
			);

			if (result.success) {
				this.toastr.info('SDK deleted successfully');
				return true;
			} else {
				this.toastr.error(result.error ? result.error : 'Failed to delete SDK');
				return false;
			}
		} finally {
			this.spinnerService.hide();
		}
	}

}